<template>
  <div class="quote-card" ref="cardRef">
    <p class="quote-card__text">
      <span class="quote-card__typed">{{ displayedQuote }}</span
      ><span class="quote-card__cursor" :class="{ 'is-blinking': isCursorBlinking }">|</span>
    </p>
    <span class="quote-card__author" :class="{ 'is-visible': isQuoteComplete }">{{
      $t('page.about.quoteCard.philosophy')
    }}</span>
    <button class="quote-card__reset" @click="restartQuoteAnimation" title="Replay animation">
      <ReplayIcon />
    </button>
  </div>
</template>

<script setup>
import { ReplayIcon } from 'hugeicons-vue'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const cardRef = ref(null)
const displayedQuote = ref('')
const isCursorBlinking = ref(true)
const isQuoteComplete = ref(false)
let quoteTimeout = null
let quoteObserver = null
let quoteAnimationStarted = false

// The quote with the typo sequence
// The quote with the typo sequence
const { tm } = useI18n()
const quoteSegments = computed(() => tm('page.about.quoteCard.segments'))

const typeSpeed = { min: 50, max: 120 } // Random typing speed for realism
const deleteSpeed = 40

const getRandomTypeSpeed = () => {
  return Math.floor(Math.random() * (typeSpeed.max - typeSpeed.min)) + typeSpeed.min
}

const animateQuote = async () => {
  isCursorBlinking.value = false
  isQuoteComplete.value = false
  displayedQuote.value = ''

  const segments = quoteSegments.value
  for (const segment of segments) {
    if (segment.type === 'type') {
      // Type each character
      for (const char of segment.text) {
        await new Promise((resolve) => {
          quoteTimeout = setTimeout(
            () => {
              displayedQuote.value += char
              resolve()
            },
            segment.isTypo ? getRandomTypeSpeed() * 0.7 : getRandomTypeSpeed()
          ) // Type faster when making typo
        })
      }
    } else if (segment.type === 'delete') {
      // Delete characters
      for (let i = 0; i < segment.text.length; i++) {
        await new Promise((resolve) => {
          quoteTimeout = setTimeout(() => {
            displayedQuote.value = displayedQuote.value.slice(0, -1)
            resolve()
          }, deleteSpeed)
        })
      }
    } else if (segment.type === 'pause') {
      // Pause (like realizing the mistake) - cursor blinks during pause
      isCursorBlinking.value = true
      await new Promise((resolve) => {
        quoteTimeout = setTimeout(() => {
          isCursorBlinking.value = false
          resolve()
        }, segment.duration)
      })
    }
  }

  // Animation complete
  isCursorBlinking.value = true
  isQuoteComplete.value = true
}

const restartQuoteAnimation = () => {
  // Clear any ongoing animation
  if (quoteTimeout) clearTimeout(quoteTimeout)

  // Restart
  animateQuote()
}

const setupObserver = () => {
  if (!cardRef.value) return

  quoteObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !quoteAnimationStarted) {
          quoteAnimationStarted = true
          // Small delay before starting
          setTimeout(() => animateQuote(), 500)
        }
      })
    },
    { threshold: 0.5 }
  )

  quoteObserver.observe(cardRef.value)
}

onMounted(() => {
  setupObserver()
})

onUnmounted(() => {
  if (quoteObserver) quoteObserver.disconnect()
  if (quoteTimeout) clearTimeout(quoteTimeout)
})
</script>

<style scoped>
.quote-card {
  @apply p-6 text-center relative w-full h-full flex flex-col justify-center;
}

.quote-card__text {
  @apply text-xl md:text-2xl font-medium italic mb-4;
  color: var(--color-text-primary);
  min-height: 4rem;
}

.quote-card__typed {
  /* Mantener el texto visible */
}

.quote-card__cursor {
  @apply inline-block;
  color: var(--color-electric-purple);
  font-weight: 400;
  animation: none;
}

.quote-card__cursor.is-blinking {
  animation: cursor-blink 1s infinite;
}

@keyframes cursor-blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

.quote-card__author {
  @apply text-sm transition-all duration-500;
  color: var(--color-text-muted);
  opacity: 0;
  transform: translateY(10px);
}

.quote-card__author.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.quote-card__reset {
  @apply absolute bottom-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  cursor: pointer;
  opacity: 0.7;
}

.quote-card__reset:hover {
  opacity: 1;
  background: var(--color-electric-purple);
  border-color: var(--color-electric-purple);
  transform: rotate(-45deg);
}

.quote-card__reset svg {
  @apply w-4 h-4;
  color: var(--color-text-secondary);
}

.quote-card__reset:hover svg {
  color: white;
}
</style>
